'use client'

import { useEffect, useState, type FormEvent } from 'react'
import { useRouter } from '@/shared/navigation'
import { isValidActivationCode, normalizeActivationCodeInput } from '@/features/auth/activationCode'
import {
  activateKitchenDevice,
  lookupActivationCode,
  type ActivationPreview,
} from '@/features/auth/api/activateDevice'
import { setKitchenDevice } from '@/shared/auth/deviceStorage'
import { clearKitchenSession } from '@/shared/auth/sessionStorage'
import { getDeviceOs, getOrCreateDeviceUid } from '@/shared/auth/deviceUid'

type SubmitState = 'idle' | 'verifying' | 'success' | 'error'
type PreviewState = 'idle' | 'loading' | 'ready' | 'error'

export function KitchenActivatePage() {
  const router = useRouter()
  const [code, setCode] = useState('')
  const [preview, setPreview] = useState<ActivationPreview | null>(null)
  const [previewState, setPreviewState] = useState<PreviewState>('idle')
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [submitState, setSubmitState] = useState<SubmitState>('idle')
  const [submitError, setSubmitError] = useState<string | null>(null)

  const isVerifying = submitState === 'verifying'
  const isSuccess = submitState === 'success'
  const isDisabled = isVerifying || isSuccess
  const canActivate = previewState === 'ready' && preview !== null && !isDisabled

  useEffect(() => {
    if (!isValidActivationCode(code)) {
      setPreview(null)
      setPreviewState('idle')
      setPreviewError(null)
      return
    }

    setPreviewState('loading')
    setPreviewError(null)

    const timer = window.setTimeout(() => {
      lookupActivationCode(code)
        .then((result) => {
          if (result.device_type !== 'kitchen_tablet') {
            throw new Error('Kode ini bukan untuk tablet dapur.')
          }
          setPreview(result)
          setPreviewState('ready')
        })
        .catch((err) => {
          setPreview(null)
          setPreviewState('error')
          setPreviewError(err instanceof Error ? err.message : 'Kode tidak dikenali.')
        })
    }, 400)

    return () => window.clearTimeout(timer)
  }, [code])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitError(null)

    if (!canActivate) return

    setSubmitState('verifying')

    try {
      const binding = await activateKitchenDevice({
        activation_code: code,
        device_uid: getOrCreateDeviceUid(),
        os: getDeviceOs(),
      })

      setKitchenDevice(binding)
      clearKitchenSession()
      setSubmitState('success')
      setTimeout(() => router.replace('/kitchen/login'), 600)
    } catch (err) {
      setSubmitState('error')
      setSubmitError(err instanceof Error ? err.message : 'Aktivasi gagal. Coba lagi.')
    }
  }

  const showDeviceInfo = isValidActivationCode(code)
  const deviceNameDisplay =
    previewState === 'loading' ? 'Memuat…' : preview?.device_name ?? '—'
  const kitchenDisplay =
    previewState === 'loading' ? 'Memuat…' : preview?.kitchen_name ?? '—'

  return (
    <main className="w-full max-w-[480px]">
      <div className="bg-surface-card rounded-xl border border-outline-variant shadow-sm overflow-hidden flex flex-col">
        <div className="bg-surface pt-8 pb-6 px-gutter-tablet sm:px-gutter-desktop flex flex-col items-center border-b border-surface-variant">
          <div className="w-16 h-16 rounded-full bg-primary-container flex items-center justify-center mb-4 text-on-primary-container shadow-sm border border-outline-variant/30">
            <span className="material-symbols-outlined text-[32px] icon-fill">skillet</span>
          </div>
          <h1 className="text-headline-md text-primary mb-2 text-center">Superoti Dapur</h1>
          <h2 className="text-headline-lg-mobile text-on-surface text-center">Aktivasi tablet dapur</h2>
        </div>

        <div className="p-gutter-tablet sm:p-gutter-desktop flex flex-col gap-8">
          <div className="text-center space-y-1">
            <p className="text-body-lg text-on-surface-variant">
              Masukkan kode aktivasi dari Admin HO untuk mengaktifkan tablet ini.
            </p>
          </div>

          <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-2">
              <label htmlFor="activation_code" className="field-label flex items-center justify-between">
                <span>Kode aktivasi</span>
                <span className="material-symbols-outlined text-base text-on-surface-variant" title="Input aman">
                  lock
                </span>
              </label>
              <input
                id="activation_code"
                name="activation_code"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                disabled={isDisabled}
                value={code}
                onChange={(e) => {
                  setCode(normalizeActivationCodeInput(e.target.value))
                  setSubmitState('idle')
                  setSubmitError(null)
                }}
                placeholder="7X9K2M"
                maxLength={6}
                className="input-code"
              />
              {previewState === 'error' && previewError && (
                <p className="text-body-sm text-status-danger">{previewError}</p>
              )}
            </div>

            {showDeviceInfo && (
              <>
                <div className="flex flex-col gap-2">
                  <label htmlFor="device_name" className="field-label">
                    Nama device
                  </label>
                  <input
                    id="device_name"
                    name="device_name"
                    type="text"
                    readOnly
                    tabIndex={-1}
                    value={deviceNameDisplay}
                    className={`input-readonly ${preview ? '' : 'is-empty'}`}
                    aria-readonly="true"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="kitchen_name" className="field-label">
                    Dapur
                  </label>
                  <input
                    id="kitchen_name"
                    name="kitchen_name"
                    type="text"
                    readOnly
                    tabIndex={-1}
                    value={kitchenDisplay}
                    className={`input-readonly ${preview ? '' : 'is-empty'}`}
                    aria-readonly="true"
                  />
                </div>
              </>
            )}

            {submitError && (
              <div role="alert" className="alert-error">
                <span className="material-symbols-outlined text-base shrink-0">error</span>
                <span>{submitError}</span>
              </div>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={!canActivate}
                className={`btn-cta btn-tactile ${isSuccess ? 'is-success' : ''}`}
              >
                {isVerifying && (
                  <>
                    <span className="material-symbols-outlined animate-spin">progress_activity</span>
                    Memverifikasi…
                  </>
                )}
                {isSuccess && (
                  <>
                    <span className="material-symbols-outlined">check_circle</span>
                    Perangkat teraktivasi
                  </>
                )}
                {!isVerifying && !isSuccess && (
                  <>
                    Aktivasi
                    <span className="material-symbols-outlined font-bold">arrow_forward</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="bg-surface-container py-4 px-gutter-tablet sm:px-gutter-desktop text-center border-t border-surface-variant">
          <p className="text-label-md text-on-surface-variant flex items-center justify-center gap-2">
            <span className="material-symbols-outlined text-sm">support_agent</span>
            Belum punya kode? Hubungi Admin HO.
          </p>
        </div>
      </div>
    </main>
  )
}
